import { Lock, LogOut, Mail } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SettingsModalLayout } from "@/features/user-setting/components/SettingsModalLayout";
import { useUser } from "@/hooks/useUser";

interface LoginInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => Promise<void>;
}

export const LoginInfoModal: React.FC<LoginInfoModalProps> = ({
  isOpen,
  onClose,
  onLogout,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useUser();

  if (!isOpen) return null;

  const handleLogoutClick = async () => {
    await onLogout();
    onClose();
  };

  return (
    <SettingsModalLayout
      isOpen={isOpen}
      onClose={onClose}
      icon={Lock}
      maxWidth="max-w-md"
      title={t("common.loginInfo")}
      footer={
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleLogoutClick}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <LogOut className="size-4" />
            {t("common.logoutAndChangePassword")}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="space-y-4">
          <div className="space-y-1">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Mail className="size-4" />
              {t("common.email")}
            </h3>
            <p className="text-sm text-gray-500">
              {t(
                "common.loginInfoDescription",
                "Your login email address is shown below.",
              )}
            </p>
          </div>

          <Input
            value={currentUser?.account?.email || ""}
            type="email"
            placeholder={t("common.email")}
            disabled
          />
        </section>

        <div className="border-t border-gray-200" />

        <section className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("common.password")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("common.passwordChangeDescription")}
            </p>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h4 className="mb-2 text-sm font-medium text-blue-900">
              {t("common.passwordChangeSteps")}
            </h4>
            <ol className="list-inside list-decimal space-y-1 text-sm text-blue-800">
              <li>{t("common.passwordChangeStep1")}</li>
              <li>{t("common.passwordChangeStep2")}</li>
              <li>{t("common.passwordChangeStep3")}</li>
              <li>{t("common.passwordChangeStep4")}</li>
            </ol>
          </div>
        </section>
      </div>
    </SettingsModalLayout>
  );
};
